import { Vector3 } from 'three';
import type { CapsuleContact, LevelCollider } from '../collision/LevelCollider';
import { MAX_VELOCITY, OVERCLIP, PLAYER_HEIGHT, PLAYER_RADIUS, SURF_NORMAL_Y } from './constants';
import { accelerate, airAccelerate, applyFriction, clipVelocity, wishDirFromInput } from './math';
import { tuning as liveTuning, type MovementTuning } from './tuning';

export interface PlayerInput {
  /** +1 forward / -1 back. */
  forward: number;
  /** +1 right / -1 left. */
  right: number;
  /** Held = auto-bhop: jumps on the first grounded tick. */
  jump: boolean;
}

/**
 * Kinematic surf controller. Implements the tick order from
 * docs/reference/MOVEMENT.md against a LevelCollider capsule query.
 * `position` is the capsule's feet (bottom point). Feel-critical constants are
 * read from `tuning` (live-editable); structural ones from `constants`.
 */
export class PlayerController {
  readonly position: Vector3;
  /** Position at the start of the current tick — for render interpolation. */
  readonly prevPosition: Vector3;
  readonly velocity = new Vector3();
  grounded = false;
  /** Airborne but riding a steep plane this tick — i.e. actually surfing. */
  surfing = false;
  /**
   * Optional transient collider queried alongside the static level — e.g. the
   * Aether Causeway ramp an ability spawns at runtime. Set/cleared by AbilityWorld.
   */
  dynamicCollider: LevelCollider | null = null;
  /**
   * Number of times this controller has respawned (killY falls + manual
   * resets). Lets observers detect a death without wrapping `respawn` — the bot
   * harness re-creates a runner on every patrol loop, and a wrapper there would
   * stack one closure per loop for the lifetime of the map.
   */
  respawnCount = 0;

  constructor(
    private readonly collider: LevelCollider,
    private readonly spawnPoint: Vector3,
    private readonly killY = -2000,
    /**
     * Feel-critical values this controller reads each tick (spec 0027). The
     * default is the live global `tuning` singleton — the player controller
     * keeps following the tuning panel, hero deltas, and timed buffs. AI
     * champions and dummies pass their own object so they never inherit the
     * player's feel or ultimates.
     */
    private readonly tuning: MovementTuning = liveTuning,
  ) {
    this.position = spawnPoint.clone();
    this.prevPosition = spawnPoint.clone();
  }

  respawn(): void {
    this.respawnCount++;
    this.position.copy(this.spawnPoint);
    this.prevPosition.copy(this.spawnPoint);
    this.velocity.set(0, 0, 0);
    this.grounded = false;
    this.surfing = false;
  }

  teleport(feet: Vector3): void {
    this.position.copy(feet);
    this.prevPosition.copy(feet);
    this.velocity.set(0, 0, 0);
    this.grounded = false;
    this.surfing = false;
  }

  /**
   * Relocate the capsule while PRESERVING velocity — the teleport-portal effect
   * (a moving player keeps their momentum through the portal). prevPosition is
   * snapped to the destination so the render lerp doesn't streak across the map.
   */
  warp(feet: Vector3): void {
    this.position.copy(feet);
    this.prevPosition.copy(feet);
    this.grounded = false;
    this.surfing = false;
  }

  /** Multiply horizontal velocity by `factor` — the speed-boost-pad effect. */
  boostLateral(factor: number): void {
    this.velocity.x *= factor;
    this.velocity.z *= factor;
  }

  /**
   * Add a flat speed `addSpeed` along the horizontal heading `yaw` (Phase Lunge's
   * arrival boost). Unlike boostLateral this still grants speed from a standstill.
   */
  surgeForward(yaw: number, addSpeed: number): void {
    const dir = wishDirFromInput(1, 0, yaw); // forward unit vector for this yaw
    this.velocity.x += dir.x * addSpeed;
    this.velocity.z += dir.z * addSpeed;
  }

  /** Capsule contacts from the static level + any dynamic collider, deepest first. */
  private gatherContacts(at: Vector3): CapsuleContact[] {
    const contacts = this.collider.capsuleContacts(at, PLAYER_RADIUS, PLAYER_HEIGHT);
    if (this.dynamicCollider) {
      contacts.push(...this.dynamicCollider.capsuleContacts(at, PLAYER_RADIUS, PLAYER_HEIGHT));
      contacts.sort((a, b) => b.depth - a.depth);
    }
    return contacts;
  }

  /**
   * Rune Blink (Vaelith ability): hop up to `maxDistance` along the current
   * heading while PRESERVING velocity — no auto-aim, no free speed. Direction is
   * the horizontal velocity if moving, else the facing (`yaw`). Advances in
   * capsule-sized probes and stops before penetrating geometry, so it can never
   * tunnel through a ramp. Returns the distance actually travelled.
   */
  blink(yaw: number, maxDistance: number): number {
    const dir = new Vector3(this.velocity.x, 0, this.velocity.z);
    if (dir.lengthSq() < 1) {
      dir.copy(wishDirFromInput(1, 0, yaw));
    }
    if (dir.lengthSq() < 1e-6) {
      return 0;
    }
    dir.normalize();

    const step = PLAYER_RADIUS / 2;
    const probe = this.position.clone();
    const next = new Vector3();
    let moved = 0;
    while (moved < maxDistance) {
      const advance = Math.min(step, maxDistance - moved);
      next.copy(probe).addScaledVector(dir, advance);
      // Reject the step if the capsule would overlap a surface at `next`.
      if (this.gatherContacts(next)[0]) {
        break;
      }
      probe.copy(next);
      moved += advance;
    }

    this.position.copy(probe);
    // Snap the render anchor so the blink reads as an instant jump, not a streak.
    this.prevPosition.copy(probe);
    return moved;
  }

  /**
   * Thunder Stomp (Brakka): force a hard downward dive to slam back onto a ramp.
   * Only ever speeds the descent — never slows an already-faster fall.
   */
  diveDown(speed: number): void {
    this.velocity.y = Math.min(this.velocity.y, -Math.abs(speed));
  }

  /**
   * Radiant Recover (Seraphine): a forgiving mid-air save — cancel the current
   * fall and add a gentle upward hop. Never reduces an existing stronger rise.
   */
  airHop(speed: number): void {
    this.velocity.y = Math.max(this.velocity.y, speed);
  }

  /** Source-style speedometer reading: horizontal speed only. */
  horizontalSpeed(): number {
    return Math.hypot(this.velocity.x, this.velocity.z);
  }

  tick(input: PlayerInput, yaw: number, dt: number): void {
    this.prevPosition.copy(this.position);

    // Jump before friction so the launch tick keeps full speed (bhop rule).
    if (this.grounded && input.jump) {
      this.velocity.y = this.tuning.jumpVelocity;
      this.grounded = false;
    }

    if (this.grounded) {
      this.velocity.y = 0; // Source semantics: no vertical velocity on ground
      applyFriction(this.velocity, this.tuning.friction, this.tuning.stopSpeed, dt);
    }

    // Ramp assist (owner-decided deviation from Source): while riding a ramp
    // face, W/S are ignored so forward/back input can't break the carve hold.
    // `surfing` lags one tick (set during the previous move phase) — fine.
    const forward = this.surfing ? 0 : input.forward;
    const wishDir = wishDirFromInput(forward, input.right, yaw);
    if (wishDir.lengthSq() > 0) {
      if (this.grounded) {
        accelerate(this.velocity, wishDir, this.tuning.maxSpeed, this.tuning.groundAccelerate, dt);
      } else {
        airAccelerate(
          this.velocity,
          wishDir,
          this.tuning.maxSpeed,
          this.tuning.airAccelerate,
          dt,
          this.tuning.airCap,
        );
      }
    }

    // Gravity applies even when grounded; the ground contact clips it away.
    // This keeps continuous contact, avoiding grounded-state flicker.
    this.velocity.y -= this.tuning.gravity * dt;

    this.velocity.x = clampAxis(this.velocity.x);
    this.velocity.y = clampAxis(this.velocity.y);
    this.velocity.z = clampAxis(this.velocity.z);

    // Substep so no move exceeds half the capsule radius — prevents tunneling
    // through ramp faces at high speed without needing swept casts.
    const travel = this.velocity.length() * dt;
    const steps = Math.min(8, Math.max(1, Math.ceil(travel / (PLAYER_RADIUS / 2))));
    this.grounded = false;
    this.surfing = false;
    for (let i = 0; i < steps; i++) {
      this.position.addScaledVector(this.velocity, dt / steps);
      this.resolveContacts();
    }

    if (this.position.y < this.killY) {
      this.respawn();
    }
  }

  private resolveContacts(): void {
    for (let pass = 0; pass < 4; pass++) {
      const contacts = this.gatherContacts(this.position);
      const contact = contacts[0]; // deepest
      if (!contact) {
        return;
      }
      this.position.addScaledVector(contact.normal, contact.depth);
      if (this.velocity.dot(contact.normal) < 0) {
        clipVelocity(this.velocity, contact.normal, OVERCLIP);
      }
      if (contact.normal.y >= SURF_NORMAL_Y) {
        this.grounded = true;
      } else if (contact.normal.y > 0.05) {
        this.surfing = true;
      }
    }
  }
}

function clampAxis(v: number): number {
  return Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, v));
}
